import { companyRequest } from '@/company/api';

/** The projection sets `status` only when the binding changed under a saved grant. */
export type ConnectionReadStatus = 'needs_review';

export interface ConnectionReadAccess {
  enabled: boolean;
  operations: string[];
  resources: string[];
  revision: string | null;
  grantVersion: string | null;
  /** The person who last saved this access, when the projection names one. */
  grantedBy?: string | null;
  status?: ConnectionReadStatus;
}

export interface ConnectionReadOperation {
  id: string;
  label: string;
}

export interface ConnectionReadResource {
  id: string;
  label: string;
}

export interface ConnectionReadTransport {
  encryption: 'tls';
  serverIdentityVerified: boolean;
}

export interface ConnectionReadItem {
  connectionId: string;
  name: string;
  provider: string;
  supportsWrites?: boolean;
  audience: 'company' | 'private' | 'people';
  status?: string | null;
  policy?: ConnectionPolicy;
  bindingVersion: string;
  operations: ConnectionReadOperation[];
  resources: ConnectionReadResource[];
  canSet: boolean;
  transport?: ConnectionReadTransport;
  access: ConnectionReadAccess;
}

export interface ConnectionPolicy {
  audience: 'private' | 'company' | 'people';
  ownerUserId: string | null;
  people: string[];
  changes: 'ask' | 'allowed';
  effectiveChanges: 'ask' | 'allowed';
  ownChanges: { allowed: boolean; grantedAt: string | null; canStop: boolean };
}

export type ConnectionAccessChoice = Pick<ConnectionPolicy, 'audience' | 'people' | 'changes'>;

export const updateConnectionPolicy = (
  item: ConnectionReadItem,
  choice: ConnectionAccessChoice,
  enabled = true,
  fetchImpl?: typeof fetch,
) =>
  companyRequest<ConnectionReadItem>(
    `/api/v1/company/connection-reads/${encodeURIComponent(item.connectionId)}/access`,
    'PUT',
    {
      ...choice,
      enabled,
      expectedRevision: item.access.revision,
      expectedBindingVersion: item.bindingVersion,
    },
    fetchImpl,
  );

export const stopOwnConnectionWrites = (item: ConnectionReadItem, fetchImpl?: typeof fetch) =>
  companyRequest<ConnectionReadItem>(
    `/api/v1/company/connection-reads/${encodeURIComponent(item.connectionId)}/own-changes`,
    'PUT',
    {
      allowed: false,
      expectedRevision: item.access.revision,
      expectedBindingVersion: item.bindingVersion,
    },
    fetchImpl,
  );

export const listConnectionReadAccess = () =>
  companyRequest<{ items: ConnectionReadItem[] }>('/api/v1/company/connection-reads');

/** How long one read may run on a database: the company's setting, the source's, the default. */
export interface ConnectionQueryLimit {
  connectionId: string;
  /** The company's own choice, or null to keep the source's limit or the default. */
  queryTimeoutSeconds: number | null;
  sourceSeconds: number | null;
  effectiveSeconds: number;
  defaultSeconds: number;
  maxSeconds: number;
}
const queryLimitPath = (connectionId: string) =>
  `/api/v1/company/connection-reads/${encodeURIComponent(connectionId)}/query-limit`;
export const readQueryLimit = (connectionId: string) =>
  companyRequest<{ item: ConnectionQueryLimit }>(queryLimitPath(connectionId)).then((r) => r.item);
export const saveQueryLimit = (connectionId: string, queryTimeoutSeconds: number | null) =>
  companyRequest<{ item: ConnectionQueryLimit }>(queryLimitPath(connectionId), 'PUT', {
    queryTimeoutSeconds,
  }).then((r) => r.item);
